import {
  buildPager,
  buildSearchStatus,
  type SearchEngine,
} from "@coveo/headless";
import { type RefObject, useEffect, useMemo, useReducer, useRef } from "react";

import styles from "./CoveoPager.module.scss";

type CoveoPagerProps = {
  engine: SearchEngine;
  resultsFocusRef: RefObject<HTMLElement | null>;
};

type PaginationSearchState = "idle" | "pending" | "loading";

function PreviousIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
    >
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NextIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
    >
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CoveoPager({ engine, resultsFocusRef }: CoveoPagerProps) {
  const pager = useMemo(
    () =>
      buildPager(engine, {
        options: {
          numberOfPages: 5,
        },
      }),
    [engine]
  );

  const searchStatus = useMemo(() => buildSearchStatus(engine), [engine]);

  const [, forceRender] = useReducer((count: number) => count + 1, 0);

  useEffect(() => pager.subscribe(forceRender), [pager]);

  const paginationSearchStateRef = useRef<PaginationSearchState>("idle");

  useEffect(() => {
    let animationFrameId: number | undefined;

    const unsubscribe = searchStatus.subscribe(() => {
      const isLoading = searchStatus.state.isLoading;
      const paginationState = paginationSearchStateRef.current;

      if (paginationState === "pending" && isLoading) {
        paginationSearchStateRef.current = "loading";
        return;
      }

      if (paginationState === "loading" && !isLoading) {
        paginationSearchStateRef.current = "idle";

        animationFrameId = window.requestAnimationFrame(() => {
          resultsFocusRef.current?.focus();
        });
      }
    });

    return () => {
      unsubscribe();

      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [resultsFocusRef, searchStatus]);

  const handlePageChange = (executePageChange: () => void) => {
    paginationSearchStateRef.current = "pending";
    executePageChange();
  };

  const { currentPage, currentPages, maxPage, hasPreviousPage, hasNextPage } =
    pager.state;

  if (currentPages.length === 0) {
    return null;
  }

  return (
    <nav className={styles.pager} aria-label="Search results pages">
      <button
        className={styles["navigation-button"]}
        type="button"
        disabled={!hasPreviousPage}
        onClick={() => handlePageChange(() => pager.previousPage())}
        aria-label="Previous page"
      >
        <PreviousIcon />
      </button>

      <div className={styles["page-buttons"]}>
        {currentPages.map((page) => {
          const isCurrentPage = page === currentPage;

          return (
            <button
              key={page}
              className={`${styles["page-button"]} ${
                isCurrentPage ? styles.active : ""
              }`}
              type="button"
              aria-current={isCurrentPage ? "page" : undefined}
              aria-label={
                isCurrentPage
                  ? `Page ${page} of ${maxPage}`
                  : `Go to page ${page} of ${maxPage}`
              }
              onClick={() => {
                if (!isCurrentPage) {
                  handlePageChange(() => pager.selectPage(page));
                }
              }}
            >
              {page}
            </button>
          );
        })}
      </div>

      <button
        className={styles["navigation-button"]}
        type="button"
        disabled={!hasNextPage}
        onClick={() => handlePageChange(() => pager.nextPage())}
        aria-label="Next page"
      >
        <NextIcon />
      </button>
    </nav>
  );
}
